import * as React from 'react';
import { Typography, Collapse } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { COLORS } from 'constants/styles';
import { PROGRAM_SETUP_ENTITIES, PROGRAM_SETUP_PRODUCT_TYPES, PRODUCT_TYPE_SLUG_TO_CODE } from 'constants/routes';
import { ROUTES } from 'constants/index';
import { getProductLineTypes } from 'application/redux/selectors';

const PRODUCT_TYPE_LABELS: Record<string, string> = {
    [PROGRAM_SETUP_PRODUCT_TYPES.COMMERCIAL_PROPERTY]: 'Commercial Property',
    [PROGRAM_SETUP_PRODUCT_TYPES.GENERAL_LIABILITY]: 'General Liability',
    [PROGRAM_SETUP_PRODUCT_TYPES.BUILDERS_RISK]: 'Builders Risk',
};

const ENTITY_LABELS: Record<string, string> = {
    [PROGRAM_SETUP_ENTITIES.SUBSCRIPTIONS]: 'Subscriptions',
    [PROGRAM_SETUP_ENTITIES.CARRIERS]: 'Carriers',
    [PROGRAM_SETUP_ENTITIES.LUMEN_PROGRAM_YEARS]: 'Lumen Program Years',
};

const ENTITY_ORDER = [
    PROGRAM_SETUP_ENTITIES.SUBSCRIPTIONS,
    PROGRAM_SETUP_ENTITIES.CARRIERS,
    PROGRAM_SETUP_ENTITIES.LUMEN_PROGRAM_YEARS,
];

const PRODUCT_TYPE_ORDER = [
    PROGRAM_SETUP_PRODUCT_TYPES.COMMERCIAL_PROPERTY,
    PROGRAM_SETUP_PRODUCT_TYPES.GENERAL_LIABILITY,
    PROGRAM_SETUP_PRODUCT_TYPES.BUILDERS_RISK,
];

// Shared base style mirroring StyledNavBase from NavItem styled-components
const navRowBase: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    width: '100%',
    cursor: 'pointer',
    boxSizing: 'border-box',
    position: 'relative',
    userSelect: 'none',
    flexShrink: 0,
};

const ProgramSetupNavSection: React.FunctionComponent = () => {
    const location = useLocation();
    const history = useHistory();
    const productLineTypes = useSelector(getProductLineTypes);
    const availableProductTypeCodes = new Set(Object.values(productLineTypes).map((t: any) => t.code));

    const availableProductTypes = PRODUCT_TYPE_ORDER.filter((pt) =>
        availableProductTypeCodes.has(PRODUCT_TYPE_SLUG_TO_CODE[pt])
    );

    const isProgramSetupActive = location.pathname.startsWith('/program-setup');

    const [programSetupOpen, setProgramSetupOpen] = React.useState(isProgramSetupActive);
    const [openProductTypes, setOpenProductTypes] = React.useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {};
        if (isProgramSetupActive) {
            PRODUCT_TYPE_ORDER.forEach((pt) => {
                initial[pt] = location.pathname.includes(`/program-setup/${pt}`);
            });
        }
        return initial;
    });

    const toggleProgramSetup = () => setProgramSetupOpen((prev: boolean) => !prev);

    const toggleProductType = (productType: string) => {
        setOpenProductTypes((prev: Record<string, boolean>) => ({
            ...prev,
            [productType]: !prev[productType],
        }));
    };

    const navigateTo = (productType: string, entity: string) => {
        history.push(ROUTES.programSetup(productType, entity));
    };

    const isEntityActive = (productType: string, entity: string) =>
        location.pathname === ROUTES.programSetup(productType, entity);

    return (
        <div>
            {/* Level 1: "Program Setup" */}
            <div
                style={{
                    ...navRowBase,
                    paddingLeft: '24px',
                    color: isProgramSetupActive ? COLORS.SECONDARY : COLORS.GRAY_MED,
                }}
                onClick={toggleProgramSetup}
            >
                <SettingsIcon fontSize="small" style={{ display: 'block', color: 'inherit', marginRight: '13px' }} />
                <Typography
                    variant="body2"
                    style={{ margin: 0, flexGrow: 1, color: isProgramSetupActive ? COLORS.SECONDARY : undefined }}
                >
                    Program Setup
                </Typography>
                {programSetupOpen ? (
                    <ExpandLess style={{ fontSize: '16px', color: COLORS.GRAY_MED, marginRight: '8px' }} />
                ) : (
                    <ExpandMore style={{ fontSize: '16px', color: COLORS.GRAY_MED, marginRight: '8px' }} />
                )}
            </div>

            <Collapse in={programSetupOpen} timeout="auto" unmountOnExit>
                {availableProductTypes.map((productType) => {
                    const isProductTypeOpen = !!openProductTypes[productType];
                    const isProductTypeActive = location.pathname.includes(`/program-setup/${productType}`);

                    return (
                        <div key={productType}>
                            {/* Level 2: product type */}
                            <div
                                style={{
                                    ...navRowBase,
                                    paddingLeft: '70px',
                                    color: isProductTypeActive ? COLORS.SECONDARY : COLORS.GRAY_MED,
                                }}
                                onClick={() => toggleProductType(productType)}
                            >
                                <Typography
                                    variant="body2"
                                    style={{
                                        margin: 0,
                                        flexGrow: 1,
                                        color: isProductTypeActive ? COLORS.SECONDARY : undefined,
                                    }}
                                >
                                    {PRODUCT_TYPE_LABELS[productType]}
                                </Typography>
                                {isProductTypeOpen ? (
                                    <ExpandLess
                                        style={{ fontSize: '14px', color: COLORS.GRAY_MED, marginRight: '8px' }}
                                    />
                                ) : (
                                    <ExpandMore
                                        style={{ fontSize: '14px', color: COLORS.GRAY_MED, marginRight: '8px' }}
                                    />
                                )}
                            </div>

                            <Collapse in={isProductTypeOpen} timeout="auto" unmountOnExit>
                                {ENTITY_ORDER.map((entity) => {
                                    const active = isEntityActive(productType, entity);
                                    return (
                                        /* Level 3: entity link */
                                        <div
                                            key={entity}
                                            style={{
                                                ...navRowBase,
                                                paddingLeft: '86px',
                                                color: active ? COLORS.SECONDARY : COLORS.GRAY_MED,
                                                backgroundColor: active ? COLORS.INFO_HIGHLIGHT : undefined,
                                                borderRadius: active ? '2px' : undefined,
                                            }}
                                            onClick={() => navigateTo(productType, entity)}
                                        >
                                            {active && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        top: 0,
                                                        bottom: 0,
                                                        width: '8px',
                                                        backgroundColor: COLORS.INFO_LIGHT,
                                                        borderRadius: '2px 0 0 2px',
                                                    }}
                                                />
                                            )}
                                            <Typography
                                                variant="body2"
                                                style={{ margin: 0, color: active ? COLORS.SECONDARY : undefined }}
                                            >
                                                {ENTITY_LABELS[entity]}
                                            </Typography>
                                        </div>
                                    );
                                })}
                            </Collapse>
                        </div>
                    );
                })}
            </Collapse>
        </div>
    );
};

export default ProgramSetupNavSection;
